import { useEffect, useState } from "react";

import { useDeployment, useWallet } from "../../context";
import { V2_FACTORY_ABI, V2_PAIR_ABI } from "../../lib/abis";
import { TOKEN_DECIMALS, ZERO_ADDRESS } from "../../lib/constants";

import type { TokenSymbol } from "../../lib/constants";
import type { Address } from "viem";

interface V2PoolPrice {
  price: number;
  reserve0: bigint;
  reserve1: bigint;
  totalSupply: bigint;
  loading: boolean;
}

export function useV2PoolPrice(
  tokenA: TokenSymbol,
  tokenB: TokenSymbol,
  active: boolean,
): V2PoolPrice | null {
  const { publicClient } = useWallet();
  const { deployment } = useDeployment();
  const [state, setState] = useState<V2PoolPrice | null>(null);

  useEffect(() => {
    if (!active) return;

    let cancelled = false;
    setState((prev) => (prev ? { ...prev, loading: true } : null));

    async function fetch() {
      if (!deployment) return;
      const tokens = deployment.tokens as unknown as Record<string, Address>;
      const addrA = tokens[tokenA.toLowerCase()];
      const addrB = tokens[tokenB.toLowerCase()];
      if (!addrA || !addrB || addrA === addrB) {
        setState(null);
        return;
      }

      try {
        const pairAddress = await publicClient.readContract({
          address: deployment.v2.router02, // use factory if available, but getPair is on factory
          abi: V2_FACTORY_ABI,
          functionName: "getPair",
          args: [addrA, addrB],
        });

        if (pairAddress === ZERO_ADDRESS) {
          setState(null);
          return;
        }

        const [reserves, totalSupply, token0Addr] = await Promise.all([
          publicClient.readContract({
            address: pairAddress,
            abi: V2_PAIR_ABI,
            functionName: "getReserves",
          }),
          publicClient.readContract({
            address: pairAddress,
            abi: V2_PAIR_ABI,
            functionName: "totalSupply",
          }),
          publicClient.readContract({
            address: pairAddress,
            abi: V2_PAIR_ABI,
            functionName: "token0",
          }),
        ]);

        if (cancelled) return;

        const aIsToken0 = token0Addr.toLowerCase() === addrA.toLowerCase();
        const decA = TOKEN_DECIMALS[tokenA] ?? 18;
        const decB = TOKEN_DECIMALS[tokenB] ?? 18;
        const resA = aIsToken0 ? reserves[0] : reserves[1];
        const resB = aIsToken0 ? reserves[1] : reserves[0];

        const numA = Number(resA) / 10 ** decA;
        const numB = Number(resB) / 10 ** decB;
        const price = numA > 0 ? numB / numA : 0;

        setState({
          price,
          reserve0: reserves[0],
          reserve1: reserves[1],
          totalSupply,
          loading: false,
        });
      } catch {
        if (!cancelled) setState(null);
      }
    }

    void fetch();
    return () => {
      cancelled = true;
    };
  }, [active, tokenA, tokenB, deployment, publicClient]);

  return state;
}
